import isPlainObject from 'lodash/isPlainObject';

import { convertToPaidStore } from '~/common/stores/domain/convertToPaid.js';
import { customTollFreeNumbersStore } from '~/common/stores/domain/customTollFreeNumbers.js';
import { lockNumberStore } from '~/common/stores/domain/lockNumber.js';
import { transferNumberStore } from '~/common/stores/domain/transferNumber.js';
import { offerSuiteStore } from '~/common/stores/domain/offerSuite.js';
import { promoCodeStore } from '~/common/stores/domain/promoCode.js';
import { flexFrameStore } from '~/common/stores/domain/flexFrame.js';
import { trialSignupStore } from '~/common/stores/domain/trialSignup';
import { userLocationStore } from '~/common/stores/domain/userLocation';
import { loginStore } from '~/common/stores/ui/loginStore';
import { annualUpgradeStore } from '~/common/stores/domain/annualUpgrade';
import { rubyStore } from '~/common/stores/domain/rubyStore';
import containErrors from '~/common/utils/containErrors.js';

import { CARD_TYPE_OBJ, ENVIRONMENT, SELECTED_NUMBER_TYPE } from '~/common/utils/consts.js';

// TRACKING FUNCTIONS

// Google Tag Manager utility
// https://developers.google.com/tag-manager/devguide
// tracking object has to match structure designated in GTM
// https://developers.google.com/tag-manager/enhanced-ecommerce#purchases
export const gtm = (trackingEvent, debug = false) => {
    window.dataLayer = window.dataLayer || [];
    try {
        if (!(trackingEvent && isPlainObject(trackingEvent))) throw Error('Tracking Event is invalid');
        if (debug) console.info(JSON.stringify(trackingEvent, null, 2)); // eslint-disable-line

        window.dataLayer.push(trackingEvent);
    } catch (err) {
        if (debug) console.error(err); // eslint-disable-line
    }
};

// script file must be present for this to work: if backend doesn't receive tracking, enable debug
export const scBeacon = (trackingEvent, debug = false) => {
    try {
        if (!window.SCBeacon) throw Error('Sitecore Beacon not found');
        const { event, data, dataKey, customParams } = trackingEvent;

        // all custom parameter names must start with 'x' (e.g. 'xKey1')
        for (const paramKey in customParams) { // eslint-disable-line
            if (paramKey[0] !== 'x') throw Error('Custom Sitecore parameter names must be camelCase, and must begin with "x"');
        }

        if (debug) console.info(`Beacon Event: '${event}', ${JSON.stringify({ data, dataKey, ...customParams }, null, 2)}`); // eslint-disable-line

        window.SCBeacon.trackEvent(event, { data, dataKey, ...customParams });
    } catch (err) {
        if (debug) console.error(err); // eslint-disable-line
    }
};


// HELPERS

// Check and Remove event helper
const checkAndRemoveEvent = (event, shouldRemoveEvent) => {
    window.dataLayer = window.dataLayer || [];
    const eventIndex = window.dataLayer.findLastIndex(obj => obj.event === event);
    if (eventIndex && shouldRemoveEvent) {
        window.dataLayer.splice(eventIndex, 1);
    }
    return eventIndex;
};


// TRACKING EVENTS

export const createPersonalizedPurchaseEvent = containErrors(status => ({
    event: 'updateSignupTransaction',
    customParams: {
        xID: offerSuiteStore.offerName || '',
        xName: lockNumberStore.selectedNumberType,
        xNumberPrefix: lockNumberStore.selectedNumberPrefix || '',
        xNumberUI: lockNumberStore.selectedNumberUI || '',
        xSearchTerm: customTollFreeNumbersStore.customTollFreeSearchTerm || '',
        xTransactionStatus: status || '',
        xTrackDateTime: (new Date()).toISOString()
    }
}));

// Direct Buy API failure
export const createSignupErrorEvent = containErrors(err => ({
    event: 'signupError',
    signupError: err
}));

export const createBillingPageDataEvent = containErrors(() => ({
    event: 'billingPageData',
    selectedPlanName: offerSuiteStore.offerName,
    selectedPlanPrice: offerSuiteStore.selectedOfferSet[offerSuiteStore.selectedPlanTerm].monthlyPlanCost,
    transferRequested: transferNumberStore.transferNumber,
    selectedNumber: lockNumberStore.selectedNumberUI,
    selectedNumberType: lockNumberStore.selectedNumberType,
    promoCode: promoCodeStore.analyticsPromoCodeName,
    promoCodeType: promoCodeStore.promoCodeType
}));

// mimics data-ga click tracking during Trial Signup process
export const createAnnualPricingEvent = containErrors(() => ({
    event: `annualPricing${offerSuiteStore.isTermMonthly ? 'Monthly' : 'Annual'}`
}));

// mimics data-ga click tracking during Trial Signup process
export const createSignupProgressEvent = containErrors((category, action) => ({
    event: 'signupProgress',
    category,
    action
}));

// successful Trial Signup
export const createTrialSignupSuccessEvent = containErrors(successObj => ({
    event: 'trialSignup',
    trialType: 'Classic',
    trialSignup: 'Success',
    trialNumberType: transferNumberStore.transferNumber ?
        'KEEP NUMBER'
        :
        (['CUSTOM TOLL FREE', 'TOLL FREE', 'LOCAL'].find(
            type => lockNumberStore.selectedNumberType.toUpperCase() === type
        ) || ''),
    trialNumberPrefix: lockNumberStore.selectedNumberPrefix || '',
    trialCustomerId: successObj.ExternalCustomerId || successObj.ExternalCustomerID
}));

export const createConversionEvent = containErrors((successObj) => {
    return {
        event: 'transactionComplete',
        paymentTerm: offerSuiteStore.selectedPlanTerm,
        numberPrefix: convertToPaidStore.isUserConvertingToPaid ? 'TRIAL NUMBER' : lockNumberStore.selectedNumberPrefix,
        // FIXME: remove afer pricing test is over
        purchaseType: `${convertToPaidStore.isUserConvertingToPaid ?
            'Trial Buy'
            :
            (trialSignupStore.freeTrialAge.length > 0) ?
                'Expired Trial'
                :
                'Direct Buy'}`,
        couponUsed: !!promoCodeStore.analyticsPromoCodeName,
        couponValue: promoCodeStore.promoCodeAmount || 0,
        revenueNoTax: offerSuiteStore.isTermMonthly ? offerSuiteStore.offerRecurringCharge : offerSuiteStore.offerRecurringCharge * 12,
        creditcardType: CARD_TYPE_OBJ[flexFrameStore.cardType],
        productType: 'Classic',
        product: offerSuiteStore.offerName,
        ecommerce: {
            currencyCode: 'USD',
            purchase: {
                actionField: {
                    id: convertToPaidStore.isUserConvertingToPaid ? successObj.ExternalCustomerID : successObj.ExternalCustomerId,
                    revenue: successObj.Charges.FirstMonthTotalCharge,
                    tax: successObj.Charges.TaxesAndSurcharges,
                    coupon: promoCodeStore.analyticsPromoCodeName
                },
                products: [
                    {
                        name: offerSuiteStore.offerName,
                        id: convertToPaidStore.isUserConvertingToPaid ?
                            'TRIAL NUMBER'
                            :
                            lockNumberStore.selectedNumberType.toUpperCase() === 'TRANSFER' ?
                                'KEEP NUMBER'
                                :
                                ['CUSTOM TOLL FREE', 'TOLL FREE', 'LOCAL'].find(
                                    type => lockNumberStore.selectedNumberType.toUpperCase() === type
                                ) || '',
                        price: successObj.Charges.PlanMonthlyFee,
                        quantity: 1
                    }
                ]
            }
        }
    };
});

// Event to be called upon plans load and when user switch between monthly/annual
export const pricingViewItemEvent = containErrors((selectedPlanTerm) => {
    const { offerSuite } = offerSuiteStore;
    const { discounts } = promoCodeStore;

    const offers = offerSuite.Offers;

    return {
        event: 'view_item',
        ecommerce: {
            // eslint-disable-next-line array-callback-return, consistent-return
            items: offers.map((offer) => {
                if (offer.Term.toLowerCase() === selectedPlanTerm) {
                    return {
                        item_brand: 'Grasshopper',
                        item_name: selectedPlanTerm === 'annual' ? `${offer.Name}_year` : `${offer.Name}_month`,
                        item_id: selectedPlanTerm === 'annual' ? `${offer.Id}_year` : `${offer.Id}_month`,
                        price: selectedPlanTerm === 'annual' ? `${offer.monthlyPlanCost * 12}` : `${offer.monthlyPlanCost}`,
                        item_category: 'Grasshopper',
                        item_category2: offer.Name,
                        item_category3: selectedPlanTerm === 'annual' ? 'YEAR' : 'MONTH',
                        item_category4: 1,
                        item_variant: 'Product',
                        quantity: 1,
                        currency: 'USD',
                        ...discounts[offer.Id] && { coupon: promoCodeStore.analyticsPromoCodeName }
                    };
                }
            }).filter(obj => !!obj)
        }
    };
});

// Event when user clicks on main CTAs
export const clickCtaEvent = containErrors((ctaName, ctaUrl, component) => ({
    event: 'click_cta',
    cta_name: ctaName,
    cta_url: ctaUrl,
    cta_component: component
}));

// Remove Cart event when user selects a different plan from the previous selected plan
export const removeFromCartEvent = containErrors(addCartEvent => ({
    event: 'remove_from_cart',
    ecommerce: {
        items: [addCartEvent]
    }
}));

// Add to cart event when user selects a plan
export const addToCartEvent = containErrors((selectedPlan) => {
    const { selectedPlanTerm } = offerSuiteStore;
    const { discounts } = promoCodeStore;

    const existingEventIndex = checkAndRemoveEvent('add_to_cart');

    const result = {
        event: 'add_to_cart',
        ecommerce: {
            items: [
                {
                    currency: 'USD',
                    item_brand: 'Grasshopper',
                    item_category: 'Grasshopper',
                    item_category2: selectedPlan[selectedPlanTerm].Name,
                    item_category3: selectedPlanTerm === 'annual' ? 'YEAR' : 'MONTH',
                    item_category4: 1,
                    item_name: selectedPlanTerm === 'annual' ? `${selectedPlan[selectedPlanTerm].Name}_year` : `${selectedPlan[selectedPlanTerm].Name}_month`,
                    item_id: selectedPlanTerm === 'annual' ? `${selectedPlan[selectedPlanTerm].Id}_year` : `${selectedPlan[selectedPlanTerm].Id}_month`,
                    item_variant: 'Product',
                    price: selectedPlanTerm === 'annual' ? `${selectedPlan[selectedPlanTerm].monthlyPlanCost * 12}` : `${selectedPlan[selectedPlanTerm].monthlyPlanCost}`,
                    quantity: 1,
                    ...discounts[selectedPlan[selectedPlanTerm].Id] && { coupon: promoCodeStore.analyticsPromoCodeName }
                }
            ]
        }
    };

    if (existingEventIndex >= 0) {
        window.dataLayer = window.dataLayer || [];
        const addCartEvent = window.dataLayer[existingEventIndex].ecommerce.items[0];
        if (addCartEvent.item_id !== `${selectedPlan[selectedPlanTerm].Id}_year` && addCartEvent.item_id !== `${selectedPlan[selectedPlanTerm].Id}_month`) {
            gtm(removeFromCartEvent(addCartEvent));
            gtm(result);
        }
    } else {
        gtm(result);
    }
});

// Start Trial Flow event when user clicks on any of the field of the Trial Form first time
export const startTrialFlowEvent = containErrors(() => {
    const numberType = SELECTED_NUMBER_TYPE[lockNumberStore.selectedNumberType];
    return {
        event: 'start_trial_flow',
        platform_product: 'ghp',
        page_type: 'SPA',
        number_type: numberType
    };
});

// Complete Trial Flow event when the Trial Form is successfully submitted
export const completeTrialFlow = containErrors((email) => {
    const numberType = SELECTED_NUMBER_TYPE[lockNumberStore.selectedNumberType];
    return {
        event: 'complete_trial_flow',
        platform_product: 'ghp',
        page_type: 'SPA',
        number_type: numberType,
        conversion_type: 'trial',
        user_email: email
    };
});

// Load Form Event when user lands on a page with form rendered
export const loadFormEvent = containErrors(formType => ({
    event: 'load_form',
    form_name: formType
}));

// Start Form Event when user focus on a form field
export const startFormEvent = containErrors(formType => ({
    event: 'start_form',
    form_name: formType
}));

// Abandon Form Event when user abandons the form without submitting it
export const abandonFormEvent = containErrors(formType => ({
    event: 'abandon_form',
    form_name: formType
}));

// Submit Form Event when the form is successfully submitted
export const submitFormEvent = containErrors(formType => ({
    event: 'submit_form',
    form_name: formType
}));

// Continue Form Event for Form progression - when a user successfully completes/fills & tabs out of a section of a multi-step form.
export const continueFormEvent = containErrors((formType, step) => ({
    event: 'continue_form',
    form_name: formType,
    form_step: step
}));

// Error Form Event when form is errored on validation or submission
export const errorFormEvent = containErrors((formType, field, errMsg) => ({
    event: 'error_form',
    form_name: formType,
    form_error_id: field,
    form_error_message: errMsg
}));

// Start Buy Flow Event when user lands on the /billing page
export const startBuyFlowEvent = containErrors(() => {
    const numberType = SELECTED_NUMBER_TYPE[lockNumberStore.selectedNumberType];
    return {
        event: 'start_buy_flow',
        platform_product: 'ghp',
        page_type: 'SPA',
        number_type: numberType
    };
});

// Complete Buy Flow Event when the user signup is success in the buy-flow
export const completeBuyFlowEvent = containErrors((signupObj, upgradeFlow) => {
    const { userInfo } = loginStore;
    const { selectedPlanTerm } = offerSuiteStore;
    const numberType = SELECTED_NUMBER_TYPE[lockNumberStore.selectedNumberType];
    const selectedTerm = selectedPlanTerm === 'annual' ? 'Annual' : 'Monthly';

    let buyType = 'direct buy';
    if (upgradeFlow) {
        buyType = upgradeFlow;
    }
    if (convertToPaidStore.isUserConvertingToPaid) {
        buyType = 'Trial conversion';
    }

    return {
        event: 'complete_buy_flow',
        platform_product: 'ghp',
        user_id: upgradeFlow ? userInfo.Id : (signupObj.ExternalCustomerId || signupObj.ExternalCustomerID),
        plan: upgradeFlow ? userInfo.PlanTerm : (signupObj.Term || selectedTerm),
        buy_type: buyType,
        conversion_type: 'purchase',
        user_email: upgradeFlow ? userInfo.Email : (signupObj.UserName || signupObj.Username),
        ...numberType && { number_type: numberType }
    };
});

// Begin Checkout Flow Event when user lands on the buy flow /numbers page and on interaction with Account Information, Billing information and Payment information in /billing page
export const beginCheckoutFlowEvent = containErrors((step, upgradeFlow) => {
    const { selectedPlanTerm, selectedOfferSet: selectedPlan } = offerSuiteStore;
    const { userInfo } = loginStore;
    const { upgradeOfferDetails } = annualUpgradeStore;
    const { discounts } = promoCodeStore;
    const numberType = SELECTED_NUMBER_TYPE[lockNumberStore.selectedNumberType];
    let item;

    if (upgradeFlow) {
        item = {
            currency: 'USD',
            item_brand: 'Grasshopper',
            item_category: 'Grasshopper',
            item_category2: userInfo.PlanName,
            item_category3: 'YEAR',
            item_category4: 1,
            item_name: `${userInfo.PlanName}_year`,
            item_id: `${userInfo.OfferID}_year`,
            item_variant: 'Product',
            price: upgradeOfferDetails.offerAmount,
            quantity: 1,
            ...promoCodeStore.analyticsPromoCodeName && { coupon: promoCodeStore.analyticsPromoCodeName },
            ...numberType && { number_type: numberType }
        };
    } else {
        item = {
            currency: 'USD',
            item_brand: 'Grasshopper',
            item_category: 'Grasshopper',
            item_category2: selectedPlan[selectedPlanTerm].Name,
            item_category3: selectedPlanTerm === 'annual' ? 'YEAR' : 'MONTH',
            item_category4: 1,
            item_name: selectedPlanTerm === 'annual' ? `${selectedPlan[selectedPlanTerm].Name}_year` : `${selectedPlan[selectedPlanTerm].Name}_month`,
            item_id: selectedPlanTerm === 'annual' ? `${selectedPlan[selectedPlanTerm].Id}_year` : `${selectedPlan[selectedPlanTerm].Id}_month`,
            item_variant: 'Product',
            price: selectedPlanTerm === 'annual' ? `${selectedPlan[selectedPlanTerm].monthlyPlanCost * 12}` : `${selectedPlan[selectedPlanTerm].monthlyPlanCost}`,
            quantity: 1,
            ...discounts[selectedPlan[selectedPlanTerm].Id] && { coupon: promoCodeStore.analyticsPromoCodeName },
            ...numberType && { number_type: numberType }
        };
    }

    return {
        event: 'begin_checkout',
        actionField: {
            step
        },
        ecommerce: {
            items: [item]
        }
    };
});

// Fatal Error Event when API responds with 400 or 500 error code
export const fatalErrorEvent = containErrors(msg => ({
    event: 'fatal_error',
    fatal_error_message: msg
}));

// Purchase Event for Successful response from server and common payment service/commerce that the transaction has been successfully processed
export const purchaseEvent = containErrors((successObj, upgradeFlow) => {
    const { selectedPlanTerm, selectedOfferSet: selectedPlan } = offerSuiteStore;
    const { userInfo } = loginStore;
    const { upgradeOfferDetails } = annualUpgradeStore;
    const numberType = SELECTED_NUMBER_TYPE[lockNumberStore.selectedNumberType];
    const { selectedRubyPlan, rubyCode } = rubyStore;
    const { discounts } = promoCodeStore;
    const isRubyUpgrade = upgradeFlow === 'add-on';
    const upgradePlanTerm = isRubyUpgrade && userInfo.PlanTerm === 'Monthly' ? 'month' : 'year';

    if (upgradeFlow) {
        return {
            event: 'purchase',
            ecommerce: {
                items: [
                    {
                        currency: 'USD',
                        item_brand: 'Grasshopper',
                        item_category: isRubyUpgrade ? `${userInfo.PlanName}-Ruby` : 'Grasshopper',
                        item_category2: userInfo.PlanName,
                        item_category3: userInfo.PlanTerm,
                        item_category4: 1,
                        item_name: isRubyUpgrade ? `${userInfo.PlanName}-Ruby_${upgradePlanTerm}` : `${userInfo.PlanName}_${upgradePlanTerm}`,
                        item_id: isRubyUpgrade ? `${selectedRubyPlan.ChargeID}_${upgradePlanTerm}` : `${userInfo.OfferID}_${upgradePlanTerm}`,
                        item_variant: isRubyUpgrade ? 'add-on' : 'Product',
                        price: isRubyUpgrade ? selectedRubyPlan.Amount : upgradeOfferDetails?.offerAmount,
                        quantity: 1,
                        ...(rubyCode || promoCodeStore.analyticsPromoCodeName) && { coupon: isRubyUpgrade ? rubyCode : promoCodeStore.analyticsPromoCodeName },
                        ...numberType && { number_type: numberType }
                    }
                ]
            },
            actionField: {
                transaction_id: userInfo.ExternalCustomerId || userInfo.ExternalCustomerID, // ToDo :: Need CyberSource Info
                shipping: 0,
                coupon: isRubyUpgrade ? rubyCode : promoCodeStore.analyticsPromoCodeName,
                tax: upgradeOfferDetails?.taxesAndSurcharges || 0,
                revenue: isRubyUpgrade ? selectedRubyPlan.Amount : upgradeOfferDetails.totalAmount
            },
            ...flexFrameStore.cardType && { payment_type: CARD_TYPE_OBJ[flexFrameStore.cardType] },
            buy_type: upgradeFlow,
            conversion_type: 'purchase',
            plan: userInfo.PlanTerm,
            user_id: userInfo.Id
        };
    }
    return {
        event: 'purchase',
        ecommerce: {
            items: [
                {
                    currency: 'USD',
                    item_brand: 'Grasshopper',
                    item_category: 'Grasshopper',
                    item_category2: selectedPlan[selectedPlanTerm].Name,
                    item_category3: selectedPlanTerm === 'annual' ? 'YEAR' : 'MONTH',
                    item_category4: 1,
                    item_name: selectedPlanTerm === 'annual' ? `${selectedPlan[selectedPlanTerm].Name}_year` : `${selectedPlan[selectedPlanTerm].Name}_month`,
                    item_id: selectedPlanTerm === 'annual' ? `${selectedPlan[selectedPlanTerm].Id}_year` : `${selectedPlan[selectedPlanTerm].Id}_month`,
                    item_variant: 'Product',
                    price: selectedPlanTerm === 'annual' ? `${selectedPlan[selectedPlanTerm].monthlyPlanCost * 12}` : `${selectedPlan[selectedPlanTerm].monthlyPlanCost}`,
                    quantity: 1,
                    ...discounts[selectedPlan[selectedPlanTerm].Id] && { coupon: promoCodeStore.analyticsPromoCodeName },
                    ...numberType && { number_type: numberType }
                }
            ]
        },
        actionField: {
            transaction_id: successObj.ExternalCustomerId || successObj.ExternalCustomerID, // ToDo :: Need CyberSource Info
            shipping: 0,
            coupon: promoCodeStore.analyticsPromoCodeName,
            tax: successObj.Charges.TaxesAndSurcharges,
            revenue: successObj.Charges.FirstMonthTotalCharge
        },
        payment_type: CARD_TYPE_OBJ[flexFrameStore.cardType],
        buy_type: convertToPaidStore.isUserConvertingToPaid ? 'Trial conversion' : 'direct buy',
        conversion_type: 'purchase',
        plan: selectedPlanTerm,
        user_id: successObj.ExternalCustomerId || successObj.ExternalCustomerID
    };
});

// Purchase Error Event for any service errors on /billing page
export const errorPurchaseEvent = containErrors((errMsg, buyType) => {
    const { selectedPlanTerm } = offerSuiteStore;
    const numberType = SELECTED_NUMBER_TYPE[lockNumberStore.selectedNumberType];
    let plan = selectedPlanTerm;
    const { userInfo } = loginStore;

    if (buyType) {
        plan = buyType === 'add-on' ? userInfo.PlanTerm.toLowerCase() : 'annual';
    }

    return {
        event: 'error_purchase',
        plan,
        buy_type: buyType || convertToPaidStore.isUserConvertingToPaid ? 'Trial conversion' : 'direct buy',
        conversion_type: 'purchase',
        ...flexFrameStore.cardType && { payment_type: CARD_TYPE_OBJ[flexFrameStore.cardType] },
        error_purchase_message: errMsg,
        ...numberType && { number_type: numberType }
    };
});

// Page view tracking
export const viewContentEvent = containErrors((context, page, contentType) => ({
    event: 'view_content',
    platform_environment: `ghp-${ENVIRONMENT[ENV]}`,
    platform_type: 'marketing',
    platform_country: userLocationStore.UserCountryCode,
    platform_locale: `en_${userLocationStore.UserCountryCode}`,
    platform_product: 'ghp',
    platform_language: 'en',
    section: context,
    sub_section: page,
    template: 'signup',
    content_type: contentType,
    page_type: 'SPA'
}));

// Promotional coupon-banner that is rendered as part of the page view
export const viewPromotionEvent = containErrors((couponCode, couponName) => ({
    event: 'view_promotion',
    ecommerce: {
        promotion_id: couponCode,
        promotion_name: couponName,
        creative_name: 'banner-coupon',
        creative_slot: 'banner'
    }
}));

// Track when user authenticated
export const userInformationEvent = containErrors(() => {
    const { userInfo } = loginStore;

    return {
        event: 'user_information',
        user_id: userInfo.Id,
        plan: userInfo.PlanTerm
    };
});

// Track Upgrade Modal
export const upgradeModalEvent = containErrors(() => {
    window.dataLayer = window.dataLayer || [];
    const contentEventIndex = checkAndRemoveEvent('view_content');
    const viewContentEventData = window.dataLayer[contentEventIndex];
    return {
        event: 'upgrade_modal',
        section: viewContentEventData.section,
        sub_section: viewContentEventData.sub_section
    };
});

// Track Login
export const loginEvent = containErrors(() => {
    return {
        event: 'login',
        platform_product: 'ghp'
    };
});
